import { useMemo } from 'react';

import { ValueFormatterFunc } from 'ag-grid-community';
import { isUndefined } from 'lodash';
import { apiHooks } from 'src/hooks/apiHooks';
import { dropUndefined } from 'src/util/list-utils';

interface IdMapper {
    [id: number]: string;
}

export interface IdMappers {
    [table: string]: ValueFormatterFunc;
}

function createMapper<T>(items: T[] | undefined, op: (item: T) => [number | undefined, string | undefined]): IdMapper {
    const mapper: IdMapper = {};
    items?.map(op).forEach(([id, name]) => {
        if (isUndefined(id) || isUndefined(name)) {
            return;
        }
        mapper[id] = name;
    });
    return mapper;
}

function useMapper<T, R>(
    response: (T | undefined)[] | undefined,
    extractor: (t: T) => [number | undefined, string | undefined]
): IdMapper {
    return useMemo(() => {
        if (isUndefined(response)) {
            return {};
        } else {
            return createMapper(dropUndefined(response), extractor);
        }
    }, [response]);
}

const mapperFrom: (mapper: IdMapper) => ValueFormatterFunc = (mapper) => {
    return (params) => {
        if (!isUndefined(mapper[params.value])) {
            return `${mapper[params.value]} (${params.value})`;
        } else {
            return params.value;
        }
    };
};

function useIdMappers(): IdMappers {
    const { useApiCall } = apiHooks;

    const { response: useTypes } = useApiCall('getUseTypes', {});
    const useTypeMapping = useMapper(useTypes?.useTypes, (useType) => [useType.useTypeId, useType.useType]);

    const { response: royaltyModelTypes } = useApiCall('getRoyaltyModelTypes', {});
    const royaltyModelTypeMapping = useMapper(royaltyModelTypes?.royaltyModelTypes, (mt) => [
        mt.royaltyModelTypeId,
        mt.royaltyModelTypeCode
    ]);

    const royaltyModels = royaltyModelTypes?.royaltyModelTypes?.flatMap((mt) => mt.royaltyModels);
    const royaltyModelMapping = useMapper(royaltyModels, (rm) => [rm?.royaltyModelId, rm?.royaltyModelCode]);

    const { response: planMetadata } = useApiCall('getPaperPlanMetadata', {});
    const planMapping = useMapper(planMetadata?.planMetadata, (item) => [
        item.paperPlan?.paperPlanId,
        item.paperPlan?.paperPlanName
    ]);

    const { response: adjustmentModels } = useApiCall('getAdjustments', {});
    const adjustmentMapping = useMapper(adjustmentModels?.adjustmentModels, (model) => [
        model.adjustmentModelId,
        model.adjustmentModel
    ]);

    const { response: rateMultiplierTypes } = useApiCall('getRateMultiplierTypes', {});
    const rateMultiplierMapping = useMapper(rateMultiplierTypes?.rateMultiplierTypes, (mType) => [
        mType.rateMultiplierTypeId,
        mType.name
    ]);

    const { response: revenueAdjustmentTypes } = useApiCall('getRevenueAdjustmentTypes', {});
    const revenueAdjustmentTypeMapping = useMapper(revenueAdjustmentTypes?.revenueAdjustmentTypes, (rat) => [
        rat.revenueAdjustmentTypeId,
        rat.revenueAdjustmentType
    ]);

    const { response: vendorShareCalculationTypes } = useApiCall('getVendorShareCalculationTypes', {});
    const vendorShareCalculationTypeMapping = useMapper(
        vendorShareCalculationTypes?.vendorShareCalculationTypes,
        (vs) => [vs.vendorShareCalculationTypeId, vs.vendorShareCalculationType]
    );

    const { response: playTypes } = useApiCall('getPlayTypes', {});
    const playTypeMapping = useMapper(playTypes?.playTypes, (playType) => [playType.playTypeId, playType.playTypeName]);

    const { response: programTypes } = useApiCall('getProgramTypes', {});
    const programTypeMapping = useMapper(programTypes?.programTypes, (programType) => [
        programType.programTypeId,
        programType.programTypeName
    ]);

    const { response: ratchetModels } = useApiCall('getRatchetModelsById', {});
    const ratchetModelMapping = useMapper(ratchetModels?.ratchetModels, (ratchetModel) => [
        ratchetModel.ratchetModelId,
        ratchetModel.ratchetModel
    ]);

    const mappers: [string, IdMapper][] = [
        ['ROYALTY_MODEL_TYPE_ID', royaltyModelTypeMapping],
        ['ROYALTY_MODEL_ID', royaltyModelMapping],
        ['PAPER_PLAN_ID', planMapping],
        ['CORE_PAPER_PLAN_ID', planMapping],
        ['ADJUSTMENT_MODEL_ID', adjustmentMapping],
        ['USE_TYPE_ID', useTypeMapping],
        ['RATE_MULTIPLIER_TYPE_ID', rateMultiplierMapping],
        ['REVENUE_ADJUSTMENT_TYPE_ID', revenueAdjustmentTypeMapping],
        ['VENDOR_SHARE_CALCULATION_TYPE_ID', vendorShareCalculationTypeMapping],
        ['PLAY_TYPE_ID', playTypeMapping],
        ['PROGRAM_TYPE_ID', programTypeMapping],
        ['RATCHET_MODEL_ID', ratchetModelMapping]
    ];

    return useMemo(
        () => {
            const idMappers: IdMappers = {};
            mappers.forEach(([name, idMap]) => (idMappers[name] = mapperFrom(idMap)));
            return idMappers;
        },
        mappers.map(([name, mapper]) => mapper)
    );
}

export const mapperHooks = { useIdMappers };
